import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useFinancialRecordsLoader } from "../../hooks/useFinancialReconciliation";
import { formatINR } from "../../utils/formatINR";
import {
  ISSUE_LABELS,
  PURPOSE_LABELS,
  csvAmount,
  downloadCSV,
  formatDay,
  formatISTTimestamp,
} from "../../utils/finance";

const TITLES = {
  received: "Money received",
  refunds: "Refunds",
  settlements: "Group settlements",
};

const SETTLEMENT_STATUS = {
  settled: "border-green-200 bg-green-50 text-green-700",
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  processing: "border-amber-200 bg-amber-50 text-amber-800",
  failed: "border-red-200 bg-red-50 text-red-700",
  skipped: "border-neutral-200 bg-neutral-100 text-neutral-600",
};

const REFUND_STATUS = {
  processed: "border-green-200 bg-green-50 text-green-700",
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  failed: "border-red-200 bg-red-50 text-red-700",
};

const Badge = ({ className, children }) => (
  <span className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${className}`}>
    {children}
  </span>
);

const Th = ({ children, right }) => (
  <th className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 whitespace-nowrap ${right ? "text-right" : "text-left"}`}>
    {children}
  </th>
);

const Td = ({ children, right, mono, className = "" }) => (
  <td className={`px-3 py-2 align-top whitespace-nowrap ${right ? "text-right tabular-nums" : ""} ${mono ? "font-mono text-[12px]" : ""} ${className}`}>
    {children}
  </td>
);

// Drill-down: the individual records behind a figure on the reconciliation page.
// `request` = { kind, fromDate, toDate, date?, groupId?, subtitle }
export default function RecordsDrawer({ request, groupNames, onClose }) {
  const loadRecords = useFinancialRecordsLoader();
  const [state, setState] = useState({ loading: true, error: "", records: null, truncated: false });

  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    setState({ loading: true, error: "", records: null, truncated: false });
    loadRecords(request)
      .then(({ records, truncated }) => !cancelled && setState({ loading: false, error: "", records, truncated }))
      .catch((err) => !cancelled && setState({ loading: false, error: err.message, records: null, truncated: false }));
    return () => {
      cancelled = true;
    };
  }, [request, loadRecords]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!request) return null;
  const groupName = (id) => groupNames[id] || `Group ${id}`;
  const { records } = state;

  const exportCSV = () => {
    const tag = request.date || `${request.fromDate}_to_${request.toDate}`;
    if (request.kind === "received") {
      downloadCSV(`money-received_${tag}.csv`, [
        ["Time (IST)", "Payment ID", "Order ID", "Purpose", "Method", "Amount", "Gateway fee", "Fee GST", "Net", "Refunded", "Credited in CMS", "Issue", "Detail", "Station", "Gun", "Vehicle"],
        ...records.map((r) => [r.created_at, r.payment_id, r.order_id, r.purpose, r.method, csvAmount(r.amount), csvAmount(r.fee), csvAmount(r.fee_gst), csvAmount(r.net), csvAmount(r.refunded), csvAmount(r.credited), r.issue, r.detail, r.station_id, r.gun_id, r.vehicle_id]),
      ]);
    } else if (request.kind === "refunds") {
      downloadCSV(`refunds_${tag}.csv`, [
        ["Time (IST)", "Refund ID", "Payment ID", "Amount", "Status", "Recorded in CMS"],
        ...records.map((r) => [r.created_at, r.refund_id, r.payment_id, csvAmount(r.amount), r.status, r.recorded ? "yes" : "no"]),
      ]);
    } else {
      downloadCSV(`settlements_${tag}.csv`, [
        ["Type", "Date", "Group", "Station", "Gun", "Charging session", "Session amount", "CMS charge (incl. GST)", "Settlement amount", "Status", "Note"],
        ...records.automatic.map((s) => ["automatic", s.settled_at || s.created_at, groupName(s.group_id), s.station_id, s.gun_id, s.charging_session_id, csvAmount(s.session_amount), csvAmount(s.cms_charge_incl_gst), csvAmount(s.amount), s.status, s.note]),
        ...records.manual.map((m) => ["manual", m.paid_on, groupName(m.group_id), m.stations.map((e) => e.station_id).join(" "), "", "", "", "", csvAmount(m.amount), "recorded", m.remarks]),
      ]);
    }
  };

  const isEmpty =
    records && (request.kind === "settlements" ? records.automatic.length + records.manual.length === 0 : records.length === 0);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={TITLES[request.kind]}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-5xl flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-neutral-200 px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ink">{TITLES[request.kind]}</h2>
            <p className="mt-0.5 text-[13px] text-neutral-500">
              {request.subtitle ||
                (request.date ? formatDay(request.date) : `${formatDay(request.fromDate)} – ${formatDay(request.toDate)}`)}
              {request.groupId ? ` · ${groupName(request.groupId)}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {records && !isEmpty && (
              <button onClick={exportCSV} className="btn btn-secondary text-[13px]">
                <Download className="h-4 w-4" />
                CSV
              </button>
            )}
            <button onClick={onClose} className="rounded-md p-2 text-neutral-500 hover:bg-neutral-100 hover:text-ink" aria-label="Close">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-4 py-4 sm:px-6">
          {state.loading && <p className="py-10 text-center text-sm text-neutral-500">Loading records…</p>}
          {state.error && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{state.error}</p>}
          {isEmpty && <p className="py-10 text-center text-sm text-neutral-500">No records for this selection.</p>}
          {state.truncated && (
            <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-[13px] text-amber-800">
              Showing the first 2,000 records. Narrow the date range to see everything.
            </p>
          )}

          {records && !isEmpty && request.kind === "received" && (
            <table className="min-w-full text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-neutral-200">
                  <Th>Time</Th><Th>Payment</Th><Th>Purpose</Th><Th right>Amount</Th><Th right>Gateway fee</Th><Th right>Net</Th><Th right>Credited in CMS</Th><Th>Check</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {records.map((r) => (
                  <tr key={r.payment_id}>
                    <Td>{formatISTTimestamp(r.created_at)}</Td>
                    <Td mono>
                      {r.payment_id}
                      {r.station_id && <div className="font-sans text-[11px] text-neutral-500">Station {r.station_id} · {r.gun_id}</div>}
                    </Td>
                    <Td>
                      {PURPOSE_LABELS[r.purpose] || r.purpose}
                      <div className="text-[11px] text-neutral-500">{r.method}</div>
                    </Td>
                    <Td right className="font-semibold text-green-700">{formatINR(r.amount)}</Td>
                    <Td right className="text-red-700">
                      {formatINR(r.fee)}
                      <div className="text-[11px] text-neutral-500">GST {formatINR(r.fee_gst)}</div>
                    </Td>
                    <Td right>{formatINR(r.net)}</Td>
                    <Td right>{r.credits > 0 ? formatINR(r.credited) : "—"}</Td>
                    <Td>
                      {r.issue ? (
                        <Badge className="border-red-200 bg-red-50 text-red-700">{ISSUE_LABELS[r.issue] || r.issue}</Badge>
                      ) : (
                        <Badge className="border-green-200 bg-green-50 text-green-700">Matched</Badge>
                      )}
                      {r.detail && <div className="mt-0.5 max-w-[16rem] whitespace-normal text-[11px] text-neutral-500">{r.detail}</div>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {records && !isEmpty && request.kind === "refunds" && (
            <table className="min-w-full text-[13px]">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-neutral-200">
                  <Th>Time</Th><Th>Refund</Th><Th>Payment</Th><Th right>Amount</Th><Th>Status</Th><Th>In CMS records</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {records.map((r) => (
                  <tr key={r.refund_id}>
                    <Td>{formatISTTimestamp(r.created_at)}</Td>
                    <Td mono>{r.refund_id}</Td>
                    <Td mono>{r.payment_id}</Td>
                    <Td right className="font-semibold text-red-700">{formatINR(r.amount)}</Td>
                    <Td><Badge className={REFUND_STATUS[r.status] || REFUND_STATUS.pending}>{r.status}</Badge></Td>
                    <Td>
                      {r.recorded ? (
                        <span className="text-neutral-600">Yes</span>
                      ) : (
                        <Badge className="border-red-200 bg-red-50 text-red-700">Missing</Badge>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {records && !isEmpty && request.kind === "settlements" && (
            <div className="space-y-6">
              {records.automatic.length > 0 && (
                <section>
                  <h3 className="section-title mb-2">Automatic settlements (Razorpay transfer per charging session)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-neutral-200">
                          <Th>Date</Th><Th>Group</Th><Th>Station</Th><Th>Charging session</Th><Th right>Session amount</Th><Th right>CMS charge</Th><Th right>Paid to group</Th><Th>Status</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {records.automatic.map((s) => (
                          <tr key={s.id}>
                            <Td>{formatISTTimestamp(s.settled_at || s.created_at)}</Td>
                            <Td>{groupName(s.group_id)}</Td>
                            <Td>{s.station_id} · {s.gun_id}</Td>
                            <Td mono>{s.charging_session_id}</Td>
                            <Td right>{formatINR(s.session_amount)}</Td>
                            <Td right>{formatINR(s.cms_charge_incl_gst)}</Td>
                            <Td right className="font-semibold text-red-700">{formatINR(s.amount)}</Td>
                            <Td>
                              <Badge className={SETTLEMENT_STATUS[s.status] || SETTLEMENT_STATUS.skipped}>{s.status}</Badge>
                              {s.note && <div className="mt-0.5 max-w-[14rem] whitespace-normal text-[11px] text-neutral-500">{s.note}</div>}
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
              {records.manual.length > 0 && (
                <section>
                  <h3 className="section-title mb-2">Manual settlements (recorded by an admin)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-neutral-200">
                          <Th>Paid on</Th><Th>Group</Th><Th>Station entries</Th><Th right>Amount</Th><Th>Remarks</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {records.manual.map((m) => (
                          <tr key={m.id}>
                            <Td>{formatDay(m.paid_on)}</Td>
                            <Td>{groupName(m.group_id)}</Td>
                            <Td className="whitespace-normal">
                              {m.stations.length === 0
                                ? "—"
                                : m.stations.map((e, i) => (
                                    <div key={i} className="tabular-nums">
                                      Station {e.station_id} · {formatDay(e.for_date)} · {formatINR(e.amount)}
                                    </div>
                                  ))}
                            </Td>
                            <Td right className="font-semibold text-red-700">{formatINR(m.amount)}</Td>
                            <Td className="whitespace-normal text-neutral-600">{m.remarks || "—"}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
